export enum OrderTypeEnum {
  PAY_INVOICE = 'pay_invoice',
  PAY_INVOICE_FOR_GOODS = 'pay_invoice_for_goods',
  PAY_INVOICE_FOR_SERVICES = 'pay_invoice_for_services',
  CARGO_WITH_DOCUMENTS = 'cargo_with_documents',
  PAY_INDIVIDUAL_CARD_CHINA = 'pay_individual_card_china',
  PAY_USDT_COLD_STORAGE = 'pay_usdt_cold_storage'
}

export enum CurrencyEnum {
  USD = 'USD',
  EUR = 'EUR',
  AED = 'AED',
  CNY = 'CNY',
  USDT = 'USDT'
}

export enum StepEnum {
  INITIAL = 'initial',
  DOCUMENTS = 'documents',
  PAYMENT = 'payment',
  COMPLETED = 'completed'
}

export enum SenderTypeEnum {
  IP = 'ip',
  OOO = 'ooo',
  SELF_EMPLOYED = 'self_employed'
}

export enum PayeeTypeEnum {
  CONTRACTOR_FROM_INVOICE = 'contractor_from_invoice',
  INPUT_MANUALLY = 'input_manually',
  NO_INVOICE = 'no_invoice'
}

export enum ContactMethodEnum {
  EMAIL = 'email',
  PHONE = 'phone',
  TELEGRAM = 'telegram'
}

export enum DocumentsStatusEnum {
  OK = 'ok',
  BAD_SIGNATURE = 'bad_signature',
  BAD_PRINT = 'bad_print',
  BAD_SIGNATURE_AND_PRINT = 'bad_signature_and_print',
  CUSTOM_PROBLEM = 'custom_problem'
}

export enum BankEnum {
  TINKOFF = 'tinkoff',
  SBER = 'sber',
  ALFA = 'alfa'
}

// Order update interface (maps to OrderUpdate in backend)
export interface OrderUpdate {
  order_type?: OrderTypeEnum | null;
  currency?: CurrencyEnum | null;
  amount?: number | null;
  payment_type?: PayeeTypeEnum | null;
  sender_type?: SenderTypeEnum | null;
}

// Base sender update interface (maps to SenderUpdate in backend)
export interface SenderUpdateBase {
  name?: string | null;
  name_en?: string | null;
  inn?: string | null;
  bank_name?: string | null;
  bank_name_en?: string | null;
  bank_bik?: string | null;
  bank_current_account?: string | null;
  bank_correspondent_account?: string | null;
  entity_address?: string | null;
  entity_address_en?: string | null;
  agreement_number?: string | null;
  agreement_date?: string | null;
}

// Specific sender update types
export interface SenderIPUpdate extends SenderUpdateBase {
  type: SenderTypeEnum.IP;
  ogrnip?: string | null;
}

export interface SenderOOOUpdate extends SenderUpdateBase {
  type: SenderTypeEnum.OOO;
  kpp?: string | null;
  ceo_name?: string | null;
  ceo_name_genitive?: string | null;
  ceo_name_en?: string | null;
  cfo_name?: string | null;
  cfo_name_genitive?: string | null;
  cfo_name_en?: string | null;
}

export interface SenderSelfEmployedUpdate extends SenderUpdateBase {
  type: SenderTypeEnum.SELF_EMPLOYED;
  legal_address?: string | null;
  legal_address_en?: string | null;
  bank_address?: string | null;
  bank_address_en?: string | null;
  email?: string | null;
  phone?: string | null;
}

export type SenderUpdate = SenderIPUpdate | SenderOOOUpdate | SenderSelfEmployedUpdate;

// Payee update interface (maps to PayeeUpdate in backend)
export interface PayeeUpdate {
  name?: string | null;
  name_en?: string | null;
  import_contract_number?: string | null;
  invoice_number?: string | null;
  address?: string | null;
  address_en?: string | null;
  account?: string | null;
  bank_name?: string | null;
  bank_name_en?: string | null;
  bank_address?: string | null;
  bank_address_en?: string | null;
  bank_swift?: string | null;
  product_items?: ProductItem[] | null;
  parsed_data_confirmed_by_user?: boolean;
}

// Base sender interface with common fields
export interface SenderBase {
  id?: number;
  name: string | null;
  name_en: string | null;
  inn: string | null;
  bank_name: string | null;
  bank_name_en: string | null;
  bank_bik: string | null;
  bank_current_account: string | null;
  bank_correspondent_account: string | null;
  entity_address: string | null;
  entity_address_en: string | null;
  signed_agreement_status: DocumentsStatusEnum | null;
  signed_agreement_problem_description: string | null;
  agreement_document_url: string | null;
  agreement_number: string | null;
  agreement_date: string | null;
  created_at?: string;
  user_id?: number;
}

// Specific sender types
export interface SenderIP extends SenderBase {
  type: SenderTypeEnum.IP;
  ogrnip: string | null;
}

export interface SenderOOO extends SenderBase {
  type: SenderTypeEnum.OOO;
  kpp: string | null;
  ceo_name: string | null;
  ceo_name_genitive: string | null;
  ceo_name_en: string | null;
  cfo_name: string | null;
  cfo_name_genitive: string | null;
  cfo_name_en: string | null;
}

export interface SenderSelfEmployed extends SenderBase {
  type: SenderTypeEnum.SELF_EMPLOYED;
  legal_address: string | null;
  legal_address_en: string | null;
  bank_address: string | null;
  bank_address_en: string | null;
  email: string | null;
  phone: string | null;
}

export type Sender = SenderIP | SenderOOO | SenderSelfEmployed;

export interface ProductItem {
  name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface Payee {
  id?: number;
  name: string | null;
  name_en: string | null;
  import_contract_number: string | null;
  invoice_number: string | null;
  address: string | null;
  address_en: string | null;
  account: string | null;
  bank_name: string | null;
  bank_name_en: string | null;
  bank_address: string | null;
  bank_address_en: string | null;
  bank_swift: string | null;
  source_file_name: string | null;
  product_items: ProductItem[] | null;
  parsed_data_confirmed_by_user: boolean;
  created_at?: string;
  user_id?: number;
}

export interface Order {
  id: number;
  order_number: number;
  order_type: OrderTypeEnum;
  currency: CurrencyEnum;
  amount: number;
  preferred_contact_method: ContactMethodEnum;
  contact_data: string;
  payment_type: PayeeTypeEnum | null;
  sender_type: SenderTypeEnum | null;
  sender_id?: number;
  payee_id?: number;
  current_step: StepEnum;
  status: string;
  created_at: string;
  sender?: Sender;
  payee?: Payee;
  amount_to_pay: number;
  official_exchange_rate: number;
  convertation_commission: number;
  swift_commission: number;
}

export interface ExchangeRate {
  currency: CurrencyEnum;
  rate: number;
  flag: string;
}

export interface User {
  id: number;
  email: string;
  bot_id: number;
}

export interface SalesPerson {
  id: number;
  name: string;
  last_name: string;
  confirm_signed_documents_flag: boolean;
}

export interface RootState {
  orders: Order[];
  currentOrder: Partial<Order>;
  senders: Sender[];
  payees: Payee[];
  exchangeRates: ExchangeRate[];
  loading: boolean;
  error: string | null;
  activeOrderStep: number;
  auth?: {
    user: User | null;
    isAuthenticated: boolean;
    isChecking: boolean;
    salesPerson: SalesPerson | null;
  };
} 